import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import * as Sc from './SelectSchoolYearBookModal.style'
import {
  BottomActionBar,
  BottomActionBarWrapper,
} from 'components/BottomActionBar'
import Portal from 'components/Portal'
import Title from 'components/Title'
import Button from 'components/Button'
import { USER_KEYS } from 'hooks/user'
import { usePostAddBook } from 'hooks/book'
import { TYPES_EXCEPTION, TYPES_SCHOOL_YEAR } from 'utils/constants'
import Radio from 'components/Radio'

const SelectSchoolYearBookModal = () => {
  const queryClient = useQueryClient()
  const { mutate: mutateAddBook } = usePostAddBook()
  const { t } = useTranslation()
  const { currentAndNextSchoolYear, invalidLicense, schoolYear, role } =
    queryClient.getQueryData({ queryKey: [USER_KEYS.GET_USER] })
  const [current, next] = currentAndNextSchoolYear || []
  const [openModal, setOpenModal] = useState(
    (schoolYear?.period === TYPES_SCHOOL_YEAR.ENDING ||
      schoolYear?.period === TYPES_SCHOOL_YEAR.CONFIGURE) &&
      !!invalidLicense?.redeemInfo?.redeemable
  )
  const [selected, setSelected] = useState('')

  const handleClickActiveLicense = (schoolYearId) => {
    mutateAddBook(
      {
        childBookId: invalidLicense?.childBookId,
        isoCountry: null,
        licenseCode: invalidLicense?.license?.code,
        schoolYearId,
        userRole: role,
      },
      {
        onSettled: () => {
          setOpenModal(false)
        },
      }
    )
  }

  if (openModal) {
    const hasBook =
      invalidLicense?.redeemInfo?.code ===
      TYPES_EXCEPTION.SCHOOL_MEMBER_ALREADY_HAS_BOOK

    let courseToActive = {
      course: `${current?.yearBegin}-${current?.yearEnd}`,
      courseID: current?.id,
    }
    if (
      hasBook &&
      invalidLicense?.redeemInfo?.additionalInfo?.schoolYearIds?.includes(
        current?.id
      )
    ) {
      courseToActive = {
        course: `${next?.yearBegin}-${next?.yearEnd}`,
        courseID: next?.id,
      }
    }

    return (
      <Portal id={'select-school-year-book-modal'}>
        <Sc.Main>
          <BottomActionBarWrapper>
            <Sc.Section>
              {hasBook ? (
                <>
                  <Title
                    preTitle={t('Ya tienes este libro')}
                    title={t(
                      '¿Quieres utilizar este libro este curso o el curso que viene?'
                    )}
                  />
                  <BottomActionBar>
                    <Button
                      classes={{ large: true }}
                      id='activate-schoolyear-button-same-book'
                      onPress={() =>
                        handleClickActiveLicense(courseToActive?.courseID)
                      }
                    >
                      {`${t('Sí, lo utilizaré el curso')} ${
                        courseToActive?.course
                      }`}
                    </Button>
                    <Button
                      classes={{ large: true, second: true }}
                      id='cancel-activation-button-same-book'
                      onPress={() => setOpenModal(false)}
                    >
                      {t('No, seguiré con el libro que ya tengo este curso')}
                    </Button>
                  </BottomActionBar>
                </>
              ) : (
                <>
                  <Title
                    title={t(
                      '¿Quieres utilizar este libro este curso o el curso que viene?'
                    )}
                  />
                  <Sc.BoxButtons>
                    <Radio
                      checked={selected === current?.id}
                      classes={{ medium: true }}
                      dataTestId={'current-schoolyear-button'}
                      id={current?.id}
                      name='courses'
                      onChange={() => setSelected(current?.id)}
                      onClick={() => setSelected(current?.id)}
                      value={current?.id}
                    >
                      {`${t('Curso actual')} ${current?.yearBegin}-${
                        current?.yearEnd
                      }`}
                    </Radio>
                    <Radio
                      checked={selected === next?.id}
                      classes={{ medium: true }}
                      dataTestId={'next-schoolyear-button'}
                      id={next?.id}
                      name='courses'
                      onChange={() => setSelected(next?.id)}
                      onClick={() => setSelected(next?.id)}
                      value={next?.id}
                    >
                      {`${t('Curso próximo')} ${next.yearBegin}-${
                        next.yearEnd
                      }`}
                    </Radio>
                  </Sc.BoxButtons>
                  <BottomActionBar>
                    <Button
                      classes={{ second: true }}
                      id='cancel-schoolyear-button'
                      onPress={() => setOpenModal(false)}
                    >
                      {t('common.actions.cancel', { keySeparator: '.' })}
                    </Button>
                    <Button
                      disabled={!selected}
                      id='accept-schoolyear-button'
                      onPress={() => handleClickActiveLicense(selected)}
                    >
                      {t('Aceptar')}
                    </Button>
                  </BottomActionBar>
                </>
              )}
            </Sc.Section>
          </BottomActionBarWrapper>
        </Sc.Main>
      </Portal>
    )
  } else {
    return null
  }
}

export default SelectSchoolYearBookModal
